import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'jalali-moment';

import { _post, _SERVER_URL_ } from '../../../utils/network';
import { commafy, toEnglishDigits } from '../../../utils/methods';
import { LocalStorage } from '../../../services';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import JalaliDateRange from "../../../components/Form/JalaliDateRange/JalaliDateRange.component";
import UserSearchSelect from "../../../components/Form/UserSearchSelect/UserSearchSelect.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const TransactionsList = () => {
	const { t } = useTranslation();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('transactions'), link: '/transactions-list' },
		{ title: t('list') }
	];

	const [transactions, setTransactions] = useState([]);
	let transactionsListData = {
		head: [t('creation_date'), t('user'), t('amount'), t('type'), t('category'), t('description')],
		body: transactions.map(transaction => [
			moment(transaction.create_date.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			<div>
				<Link className="d-block" to={`/users-edit/${transaction.user_id_information?.user_id || transaction.user_id}`} target="_blank">
					{transaction.user_id_information?.user_fullname || '—'}
				</Link>
				<small className="d-block text-muted">
					{t('national_code')}: <bdi dir="ltr">{transaction.user_id_information?.national_code || '—'}</bdi>
				</small>
				<small className="d-block text-muted">
					{t('inviter_code')}: <bdi dir="ltr">{transaction.user_id_information?.inviter_code || '—'}</bdi>
				</small>
			</div>,
			<span className={transaction.transaction_amount < 0 ? 'text-danger' : 'text-success'}>
				{commafy(transaction.transaction_amount)}
			</span>,
			!!transaction.transaction_type ? t('score2_name') : t('score1_name'),
			transaction.transactioncategory_id_information.transactioncategory_name,
			transaction.transaction_description
		]),
		directions: ['ltr', 'rtl', 'ltr', '', 'rtl', 'rtl']
	};
	const [pageNumber, setPageNumber] = useState(1);
	const [pageCounter] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [pagesCount, setPagesCount] = useState(0);
	const [searchTransactionCategory, setSearchTransactionCategory] = useState(null);
	const [searchTransactionType, setSearchTransactionType] = useState(null);
	const [selectedUser, setSelectedUser] = useState(null);
	const [searchUserId, setSearchUserId] = useState(null);
	const [searchDates, setSearchDates] = useState({ start_date: null, end_date: null });
	const [excelIsLoading, setExcelIsLoading] = useState(false);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchCounter, setSearchCounter] = useState(0);
	const getTransactions = async () => {
		let body = {
			user_id: searchUserId,
			transaction_type: searchTransactionType,
			transactioncategory_id: searchTransactionCategory,
			...searchDates,
			page: pageNumber,
			count: pageCounter
		};
		setSearchButtonIsLoading(true);
		let response = await _post('/transactions/admin/getAll', body);
		setSearchButtonIsLoading(false);
		switch(response?.code){
			case 200:
				setTotalCount(response.data.all_transactions);
				setTransactions(response.data.transactions);
				break;
			default:
				toast.error(response?.[t('api_message')] || t('transactions_request_failed'));
		}
	};
	
	const readFilters = form => {
		const dates = {};
		for (const [field, key, message] of [
			['searchTransactionStartDate', 'start_date', 'enter_start_date_correctly'],
			['searchTransactionEndDate', 'end_date', 'enter_end_date_correctly']
		]) {
			if (form.elements[field].dataset.incomplete === 'true') {
				toast.warn(t(message));
				return null;
			}
			const value = toEnglishDigits(form.elements[field].value.trim());
			const date = value ? moment(value, 'jYYYY/jMM/jDD', true) : null;
			if (value && (!/^\d{4}\/\d{2}\/\d{2}$/.test(value) || !date.isValid() || date.format('jYYYY/jMM/jDD') !== value)) {
				toast.warn(t(message));
				return null;
			}
			dates[key] = date ? date.locale('en').format('YYYY-MM-DD') : null;
		}
		if (dates.start_date && dates.end_date && dates.start_date > dates.end_date) {
			toast.warn(t('invalid_date_range'));
			return null;
		}
		return {
			transactioncategory_id: form.elements.searchTransactionCategory.value.trim() || null,
			transaction_type: form.elements.searchTransactionType.value.trim() || null,
			user_id: selectedUser?.value || null,
			...dates
		};
	};

	const getTransactionsExcel = async event => {
		if (excelIsLoading) return;
		const filters = readFilters(event.currentTarget.form);
		if (!filters) return;
		setExcelIsLoading(true);
		try {
			const response = await fetch(`${_SERVER_URL_}/download-excel-transactions`, {
				method: 'POST',
				headers: { Token: LocalStorage.get('token') || '', 'Content-Type': 'application/json' },
				body: JSON.stringify(filters)
			});
			if (!response.ok) {
				const payload = await response.json().catch(() => null);
				toast.error(payload?.[t('api_message')] || t('transactions_request_failed'));
				return;
			}
			const url = window.URL.createObjectURL(await response.blob());
			const link = document.createElement('a');
			link.href = url;
			link.download = 'transactions.xlsx';
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
		} catch {
			toast.error(t('transactions_request_failed'));
		} finally {
			setExcelIsLoading(false);
		}
	};

	const filterTransactions = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return 0;
		const filters = readFilters(event.currentTarget);
		if (!filters) return;
		setSearchTransactionCategory(filters.transactioncategory_id);
		setSearchTransactionType(filters.transaction_type);
		setSearchUserId(filters.user_id);
		setSearchDates({ start_date: filters.start_date, end_date: filters.end_date });
		if(pageNumber === 1)
			setSearchCounter(searchCounter + 1);
		else
			setPageNumber(1);
	};

	const transactionTypesList = [
		{ key: t('score1_name'), value: 0 },
		{ key: t('score2_name'), value: 1 }
	];
	
	const [transactionCategories, setTransactionCategories] = useState([]);
	const getTransactionCategories = async () => {
		let body = {
			transactioncategory_name: null,
			page: 1,
			count: 1000000
		};
		let response = await _post('/transactioncategories/admin/getAll', body);
		switch(response.code){
			case 200:
				setTransactionCategories(response.data.transactioncategories.map(item => {
					return {
						key: item.transactioncategory_name,
						value: item.transactioncategory_id
					}
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	useEffect(() => {
		getTransactionCategories();
	}, []);

	useEffect(() => {
		setPagesCount(Math.ceil(totalCount / pageCounter));
	}, [totalCount]);
	
	useEffect(() => {
		setSearchCounter(searchCounter + 1);
	}, [pageNumber]);

	useEffect(() => {
		getTransactions();
	}, [searchCounter]);
	
	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('search_for')} {t('transactions')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterTransactions}>
						<div className="row gx-2">
							<FormField type="select" fieldKey="searchTransactionCategory" title={t('category')} value={searchTransactionCategory} selectItems={transactionCategories} isSearch={true} />
							<FormField type="select" fieldKey="searchTransactionType" title={t('type')} value={searchTransactionType} selectItems={transactionTypesList} isSearch={true} />
							<UserSearchSelect fieldKey="searchTransactionUser" title={t('user')} value={selectedUser} onChange={setSelectedUser} />
							<JalaliDateRange fromFieldKey="searchTransactionStartDate" toFieldKey="searchTransactionEndDate" />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
							<LoadingButton className="btn btn-success d-inline-block mt-3 me-2" type="button" onClick={getTransactionsExcel} isLoading={excelIsLoading} disabled={excelIsLoading}>
								<i className="fa fa-download"></i> {t('get_excel')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('transactions')} pageNumber={pageNumber} pageCounter={pageCounter} totalCount={totalCount} data={transactionsListData} />
			<Pagination pagesCount={pagesCount} action={setPageNumber} />
		</>
	);
};

export default TransactionsList;
